/**
 * Mobile header navigation - hamburger menu & megamenu nav links
 * Fixes tap responsiveness and prevents unwanted redirects on dropdown links
 */
(function () {
  'use strict';

  const OVERLAY_CLASSES = ['show-search', 'show-filters', 'show-minicart'];
  // The drawer CSS is @media (max-width: 1023px) and the theme bundle treats
  // "desktop" as >= 1024, so anything below 1024 is the click-driven drawer. This
  // used to be 768, which left 769-1023px with no handler on either side: the
  // bundle's hover logic was inactive and these click handlers bailed out, so nav
  // links navigated instead of opening their panel and the back button did nothing.
  const MOBILE_BREAKPOINT = 1023;

  /**
   * Checks if the viewport is at or below the mobile breakpoint.
   * @returns {boolean} True if viewport width <= MOBILE_BREAKPOINT (1023px)
   */
  function isMobile() {
    return window.matchMedia('(max-width: ' + MOBILE_BREAKPOINT + 'px)').matches;
  }

  function clearOverlays() {
    OVERLAY_CLASSES.forEach(function (cls) {
      document.body.classList.remove(cls);
    });
  }

  /**
   * Initializes the hamburger menu toggle. Attaches a click handler that
   * opens/closes the mobile sub-nav overlay and shows the first nav panel.
   */
  let hamburgerBound = false;

  function initHamburger() {
    // Bound once, delegated from document in the capture phase: it must run
    // before the theme bundle's own hamburger handler (which would double-toggle),
    // and element-level binding order flips after a Turbolinks body swap.
    if (hamburgerBound) return;
    hamburgerBound = true;

    function handleHamburgerClick(e) {
      if (!e.target.closest('.header--hamburger')) return;
      e.preventDefault();
      e.stopImmediatePropagation();

      const nav = document.querySelector('nav');
      const navWrapper = document.querySelector('nav .nav--wrapper');

      if (document.body.classList.contains('show-sub-nav')) {
        document.body.classList.remove('show-sub-nav');
        if (nav) nav.removeAttribute('data-sub-nav');
      } else {
        clearOverlays();
        document.body.classList.add('show-sub-nav');
        if (nav && navWrapper) {
          const firstLink = navWrapper.querySelector('.nav--link');
          const index = firstLink
            ? Array.from(navWrapper.querySelectorAll('.nav--link')).indexOf(firstLink) + 1
            : 1;
          nav.setAttribute('data-sub-nav', String(index));
        }
      }
    }

    document.addEventListener('click', handleHamburgerClick, true);
  }

  /**
   * Initializes mobile nav link handlers. On mobile, prevents dropdown links
   * (with data-turbolinks) from navigating; instead opens their sub-nav panel.
   */
  function initMobileNavLinks() {
    const nav = document.querySelector('nav');
    const navWrapper = document.querySelector('nav .nav--wrapper');
    if (!nav || !navWrapper) return;

    navWrapper.addEventListener(
      'click',
      function (e) {
        const link = e.target.closest('.nav--link');
        if (!link || !isMobile()) return;

        if (link.getAttribute('data-turbolinks')) {
          e.preventDefault();
          e.stopImmediatePropagation();

          const links = navWrapper.querySelectorAll('.nav--link');
          const index = Array.from(links).indexOf(link) + 1;

          clearOverlays();
          document.body.classList.add('show-sub-nav');
          nav.setAttribute('data-sub-nav', String(index));
        }
      },
      true
    );
  }

  /**
   * Submenu back control: returns to the first top-level panel (data-sub-nav="1").
   */
  function initSubNavBack() {
    const nav = document.querySelector('nav');
    if (!nav) return;

    nav.addEventListener(
      'click',
      function (e) {
        const btn = e.target.closest('.sub-nav--back');
        if (!btn || !isMobile()) return;

        e.preventDefault();
        e.stopImmediatePropagation();
        nav.setAttribute('data-sub-nav', '1');
      },
      true
    );
  }

  /**
   * Sticky header state: marks the body when the MILQ header is detached from
   * its top-of-page position (the cream spacer has scrolled out of view).
   * CSS keys off body.milq-header-stuck to give the pinned mobile header a
   * solid background when it slides back in on scroll up.
   */
  let stickyStateBound = false;

  function updateStickyState() {
    // Queried each time: Turbolinks swaps <body>, so cached nodes go stale.
    if (!document.querySelector('[data-milq-header]')) return;
    const spacer = document.querySelector('.milq-header-spacer');
    // No spacer (hidden via theme setting): the header floats over content
    // from the very top, so any scroll counts as detached.
    const stuck = spacer
      ? spacer.getBoundingClientRect().bottom < 0
      : window.scrollY > 0;
    document.body.classList.toggle('milq-header-stuck', stuck);
  }

  function initStickyState() {
    updateStickyState();

    // The scroll listener lives on window and survives Turbolinks body swaps —
    // bind it once, not on every init.
    if (stickyStateBound) return;
    stickyStateBound = true;

    let ticking = false;
    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          updateStickyState();
        });
      },
      { passive: true }
    );
  }

  function init() {
    initHamburger();
    initMobileNavLinks();
    initSubNavBack();
    initStickyState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('turbolinks:load', init);
})();
